import { Component } from 'vue-property-decorator'

import { BootstrapImage } from '../../dsl/front'

import { AbstractModuleUi, FetchesRelatedAsync } from '../_abstract/ui'
import { Image, ImageFile } from '../_contracts'
import { prepareImage } from '../_filters'

import { PresentationModule } from './Presentation.contracts'

@Component<PresentationModuleUi>({
  name: 'PresentationModuleUi',
  components: { BootstrapImage },
  async created () {
    await this.fetchRelated()
  },
  template: `
    <section class="presentation-module" :class="[themeClass, sizeClass, debugClass]" :id="module.content.anchor" :style="cssVariables">
      <div class="container">
        <div class="presentation-module__content">
          <div v-if="hasTextCol" class="presentation-module__text" :class="textOrderClass">
            <h2 v-if="module.content.heading" v-html="processedHeading" />
            <div v-if="module.content.text" v-html="module.content.text" />
            <div v-if="module.content.linkLabel && module.content.linkHref" class="presentation-module__link">
              <a :href="module.content.linkHref">{{ module.content.linkLabel }}</a>
            </div>
          </div>
          <div v-if="hasImageCol" class="presentation-module__image" :class="imageOrderClass">
            <BootstrapImage v-if="image && image.src" v-bind="image" :isLazy="false" />
          </div>
        </div>
      </div>
    </section>
  `
})
export class PresentationModuleUi extends AbstractModuleUi<PresentationModule> implements FetchesRelatedAsync {
  public image: Image | null = null

  public async fetchRelated (): Promise<void> {
    if (this.module.content.image === null) {
      this.onReady()
      return
    }
    try {
      const imageFile = await this.relatedService.resolve(this.module.content.image, {}) as ImageFile

      if (!imageFile) {
        this.onReady()
        return
      }

      const maxWidth: number | undefined = this.isMobile ? 768 : 1220
      this.image = prepareImage(imageFile, this.imageRatio, maxWidth, this.module.isLazyLoaded)

      if (!this.image || !this.image.src) {
        this.onError(new Error('Invalid image object'))
        return
      }

      this.onReady()
    } catch (e) {
      this.onError(e)
    }
  }

  public get hasTextCol (): boolean {
    return this.textWidthPercent > 0
  }

  public get hasImageCol (): boolean {
    return this.imageWidthPercent > 0 && !!this.module.content.image
  }

  public get processedHeading (): string {
    if (!this.module.content.heading) {
      return ''
    }

    return this.module.content.heading.replace(/\*([^*]+)\*/g, '<span>$1</span>')
  }

  private get textWidthPercent (): number {
    return this.clamp(this.module.content.textWidthPercent, 0, 100)
  }

  private get imageWidthPercent (): number {
    return 100 - this.textWidthPercent
  }

  public get cssVariables (): string {
    return `--text-width: ${this.textWidthPercent}; --image-width: ${this.imageWidthPercent}; --has-image: ${this.hasImageCol ? 1 : 0};`
  }

  public get textOrderClass (): string {
    return this.module.content.side === 'left' ? '--order-1' : '--order-2'
  }

  public get imageOrderClass (): string {
    return this.module.content.side === 'left' ? '--order-2' : '--order-1'
  }

  private clamp (value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value))
  }
}
